import React from 'react';
import { Link } from 'react-router-dom';
import PaintingHeader from './PaintingHeader';
import PaintingFooter from './PaintingFooter';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { MapPin, Phone, CheckCircle, Navigation } from 'lucide-react';

const ServiceAreas = () => {
  const primaryCities = [
    {
      name: "Hollywood",
      zip: "33019, 33020, 33021, 33023, 33024",
      description: "Our home base! Serving all Hollywood neighborhoods with fast response times.",
      featured: true
    },
    {
      name: "Fort Lauderdale",
      zip: "33301-33334",
      description: "Complete residential and commercial painting services throughout Fort Lauderdale.",
      featured: true
    },
    {
      name: "Pembroke Pines",
      zip: "33024-33029",
      description: "Expert painting for homes and businesses in Pembroke Pines and surrounding areas.",
      featured: true
    }
  ];

  const otherCities = [
    "Miramar",
    "Hallandale Beach",
    "Aventura",
    "Davie",
    "Plantation",
    "Sunrise",
    "Weston",
    "Cooper City",
    "Dania Beach",
    "Lauderhill",
    "Tamarac",
    "Coral Springs",
    "Pompano Beach",
    "Deerfield Beach",
    "Boca Raton",
    "Delray Beach",
    "Coconut Creek",
    "Parkland",
    "North Miami Beach",
    "Sunny Isles Beach"
  ];

  const neighborhoods = [
    "West Hollywood",
    "East Hollywood",
    "Hollywood Beach",
    "Hollywood Lakes",
    "Emerald Hills",
    "Lake Forest",
    "Liberia",
    "Park East",
    "Washington Park",
    "Downtown Hollywood",
    "Hollywood Hills",
    "The Circles"
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <PaintingHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-semibold mb-4">
            📍 Service Areas
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none mb-6">
            Serving South Florida
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Professional painting services throughout Broward County and surrounding areas. Based in Hollywood, FL.
          </p>
        </div>
      </section>

      {/* Primary Service Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#F1F5F9] rounded-full text-sm font-bold text-[#1E3A8A] uppercase tracking-wider mb-4">
              Primary Service Areas
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A] mb-4">
              Where We Work
            </h2>
            <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
              Fast response times and personalized service in our core coverage area
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {primaryCities.map((city) => (
              <Card key={city.name} className="border-2 hover:border-[#1E3A8A] transition-colors hover:shadow-xl">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-[#1E3A8A] rounded-2xl flex items-center justify-center mb-6">
                    <MapPin className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-black text-[#0F172A] mb-2">
                    {city.name}
                  </h3>
                  <p className="text-sm text-[#64748B] mb-4 font-mono">
                    ZIP: {city.zip}
                  </p>
                  <p className="text-[#64748B] leading-relaxed">
                    {city.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Hollywood Neighborhoods */}
      <section className="py-20 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A] mb-4">
              Hollywood Neighborhoods We Serve
            </h2>
            <p className="text-lg text-[#64748B]">
              Local expertise in every Hollywood community
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {neighborhoods.map((neighborhood) => (
              <div 
                key={neighborhood} 
                className="bg-white rounded-lg p-4 flex items-center space-x-3 border-2 hover:border-[#1E3A8A] transition-colors"
              >
                <CheckCircle className="w-5 h-5 text-[#EA580C] flex-shrink-0" />
                <span className="font-semibold text-[#0F172A]">{neighborhood}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Service Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A] mb-4">
              Additional Service Areas
            </h2>
            <p className="text-lg text-[#64748B]">
              We also serve these Broward and Miami-Dade communities
            </p>
          </div>

          <Card className="border-2">
            <CardContent className="p-8">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {otherCities.map((city) => (
                  <div key={city} className="flex items-center space-x-2">
                    <Navigation className="w-4 h-4 text-[#1E3A8A] flex-shrink-0" />
                    <span className="text-[#0F172A]">{city}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A] mb-4">
              Find Us
            </h2>
            <p className="text-lg text-[#64748B]">
              Based in Hollywood, serving all of South Florida
            </p>
          </div>

          <div className="aspect-video bg-white rounded-2xl overflow-hidden shadow-2xl border-2">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3585.4537!2d-80.149!3d26.011!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDAwJzQwLjAiTiA4MMKwMDgnNTYuNCJX!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="D'este Painting Service Area"
            ></iframe>
          </div>

          <div className="mt-12 text-center">
            <Card className="inline-block border-2">
              <CardContent className="p-8">
                <MapPin className="w-12 h-12 text-[#1E3A8A] mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[#0F172A] mb-2">
                  D'este Painting and Service
                </h3>
                <p className="text-[#64748B] mb-4">
                  1841 Rodman St<br />
                  Hollywood, FL 33020
                </p>
                <a 
                  href="https://maps.google.com/?q=1841+Rodman+St,Hollywood,FL+33020" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#1E3A8A] hover:text-[#EA580C] font-semibold"
                >
                  Get Directions →
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Coverage Info */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-[#0F172A] mb-6 text-center">
                Don't See Your City?
              </h3>
              <p className="text-lg text-[#64748B] text-center mb-6">
                We're always expanding our service area! If you're located in South Florida and need professional painting services, give us a call. We'd be happy to discuss your project.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:+19548172046">
                  <Button size="lg" className="w-full sm:w-auto bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
                    <Phone className="w-5 h-5 mr-2" />
                    Call (954) 817-2046
                  </Button>
                </a>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#F1F5F9]">
                    Request Quote
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Professional painting services in your neighborhood. Free quotes, fast response!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="w-full sm:w-auto bg-[#EA580C] hover:bg-[#C2410C] text-white text-lg px-8 py-6">
                Request Free Quote
              </Button>
            </Link>
            <a href="tel:+19548172046">
              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white text-[#1E3A8A] hover:bg-white/90 text-lg px-8 py-6">
                <Phone className="w-5 h-5 mr-2" />
                Call (954) 817-2046
              </Button>
            </a>
          </div>
        </div>
      </section>

      <PaintingFooter />
    </div>
  );
};

export default ServiceAreas;